// Frontend content management utilities
export const PAGES_STORAGE_KEY = 'oha_pages_data';
export const CONTACT_STORAGE_KEY = 'oha_contact_data';
export const SERVICES_STORAGE_KEY = 'oha_services_data';
export const VALUES_STORAGE_KEY = 'oha_values_data';
export const TEAM_STORAGE_KEY = 'oha_team_data';
export const JOURNEY_STORAGE_KEY = 'oha_journey_data';
export const CASE_STUDIES_STORAGE_KEY = 'oha_case_studies_data';
export const BLOG_POSTS_STORAGE_KEY = 'oha_blog_posts_data';

// Default data functions - MUST be defined before they are used
const getDefaultServices = () => [
  {
    id: '1',
    icon: 'Brain',
    title: 'Agentic AI Solutions',
    description: 'Implement autonomous AI agents that revolutionize your business processes.',
    features: [
      'Custom AI Agent Development',
      'Process Automation',
      'Intelligent Decision Making',
      'Real-time Analytics',
      '24/7 AI Support Systems'
    ],
    price: 'Starting at $15,000',
    popular: true
  },
  {
    id: '2',
    icon: 'Shield',
    title: 'IT Consulting',
    description: 'Comprehensive technology strategy and infrastructure modernization.',
    features: [
      'Technology Assessment',
      'Infrastructure Planning',
      'Security Implementation',
      'Cloud Migration',
      'System Integration'
    ],
    price: 'Starting at $5,000',
    popular: false
  },
  {
    id: '3',
    icon: 'Target',
    title: 'Business Strategy',
    description: 'Data-driven strategic planning for accelerated growth and efficiency.',
    features: [
      'Strategic Planning',
      'Market Analysis',
      'Performance Optimization',
      'Growth Strategy',
      'ROI Maximization'
    ],
    price: 'Starting at $8,000',
    popular: false
  }
];

const getDefaultValues = () => [
  {
    id: '1',
    icon: 'Target',
    title: 'Innovation',
    description: 'We stay at the forefront of technology to deliver cutting-edge solutions that give our clients a competitive advantage.'
  },
  {
    id: '2',
    icon: 'Heart',
    title: 'Client Success',
    description: 'Your success is our success. We are committed to delivering results that exceed expectations and drive real business value.'
  },
  {
    id: '3',
    icon: 'Award',
    title: 'Excellence',
    description: 'We maintain the highest standards in everything we do, from strategy development to implementation and support.'
  },
  {
    id: '4',
    icon: 'Users',
    title: 'Partnership',
    description: 'We believe in building long-term partnerships with our clients, working together to achieve sustainable growth.'
  }
];

const getDefaultTeam = () => [
  {
    id: '1',
    name: 'Alex Chen',
    role: 'CEO & AI Strategist',
    bio: '15+ years in AI and business strategy. Former Microsoft AI lead with expertise in enterprise AI implementation.',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face'
  },
  {
    id: '2',
    name: 'Sarah Rodriguez',
    role: 'CTO & Technical Lead',
    bio: 'Expert in scalable AI systems and cloud infrastructure. Previously at Google, specializing in agentic AI solutions.',
    image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face'
  },
  {
    id: '3',
    name: 'Michael Johnson',
    role: 'Business Strategy Director',
    bio: 'MBA from Stanford, 12+ years in management consulting. Helped 200+ startups scale from seed to Series A.',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=300&h=300&fit=crop&crop=face'
  }
];

const getDefaultJourney = () => [
  {
    id: '1',
    year: '2020',
    event: 'Founded OHA System with a vision to democratize AI for small businesses'
  },
  {
    id: '2',
    year: '2021',
    event: 'Launched our first agentic AI solution, helping 20+ businesses automate operations'
  },
  {
    id: '3',
    year: '2022',
    event: 'Expanded to serve 100+ clients across various industries'
  },
  {
    id: '4',
    year: '2023',
    event: 'Achieved 200% average ROI for clients through strategic AI implementations'
  },
  {
    id: '5',
    year: '2024',
    event: 'Recognized as a leading AI consulting firm with 99.9% client satisfaction rate'
  }
];

const getDefaultCaseStudies = () => [
  {
    id: '1',
    title: 'TechStart Inc. - AI-Powered Customer Service Revolution',
    industry: 'SaaS Technology',
    challenge: 'Manual customer support was overwhelming their small team, leading to delayed responses and customer churn.',
    solution: 'Implemented an intelligent AI agent system that handles 80% of customer inquiries autonomously while seamlessly escalating complex issues to human agents.',
    results: [
      { metric: '300%', description: 'Increase in customer satisfaction' },
      { metric: '75%', description: 'Reduction in response time' },
      { metric: '$500K', description: 'Annual cost savings' },
      { metric: '90%', description: 'First-contact resolution rate' }
    ],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    testimonial: {
      text: "OHA System's AI solution transformed our customer service from a cost center into a competitive advantage. We now handle 5x more customers with the same team size.",
      author: "Sarah Johnson",
      role: "CEO, TechStart Inc."
    }
  },
  {
    id: '2',
    title: 'GrowthCorp - Strategic Business Transformation',
    industry: 'E-commerce',
    challenge: 'Struggling to scale operations efficiently while maintaining quality and customer experience during rapid growth.',
    solution: 'Developed a comprehensive growth strategy with AI-powered inventory management and predictive analytics for demand forecasting.',
    results: [
      { metric: '250%', description: 'Revenue growth in 18 months' },
      { metric: '40%', description: 'Reduction in inventory costs' },
      { metric: '95%', description: 'Order fulfillment accuracy' },
      { metric: '15x', description: 'Increase in operational efficiency' }
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    testimonial: {
      text: "The strategic roadmap and AI implementations provided by OHA System enabled us to scale from $2M to $10M in revenue while maintaining our quality standards.",
      author: "Michael Chen",
      role: "Founder, GrowthCorp"
    }
  }
];

const getDefaultBlogPosts = () => [
  {
    id: '1',
    title: 'The Future of Agentic AI: Transforming Business Operations in 2024',
    excerpt: 'Discover how autonomous AI agents are revolutionizing the way businesses operate, make decisions, and serve customers. Learn about the latest trends and practical implementation strategies.',
    author: 'Alex Chen',
    date: '2024-01-15',
    readTime: '8 min read',
    category: 'AI Strategy',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop',
    featured: true,
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit...'
  },
  {
    id: '2',
    title: 'Small Business Digital Transformation: A Step-by-Step Guide',
    excerpt: 'Learn how small businesses can successfully navigate digital transformation without overwhelming their resources or disrupting operations.',
    author: 'Sarah Rodriguez',
    date: '2024-01-12',
    readTime: '6 min read',
    category: 'Digital Transformation',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
    featured: false,
    content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit...'
  }
];

// Get page content from localStorage
export const getPageContent = (page) => {
  try {
    const pagesData = JSON.parse(localStorage.getItem(PAGES_STORAGE_KEY) || '{}');
    return pagesData[page] || getDefaultPageContent(page);
  } catch (error) {
    return getDefaultPageContent(page);
  }
};

// Get contact data from localStorage
export const getContactInfo = () => {
  try {
    const contactData = JSON.parse(localStorage.getItem(CONTACT_STORAGE_KEY) || '{}');
    return contactData.email ? contactData : getDefaultContactInfo();
  } catch (error) {
    return getDefaultContactInfo();
  }
};

// Update page content in localStorage
export const updatePageContent = (page, content) => {
  try {
    const pagesData = JSON.parse(localStorage.getItem(PAGES_STORAGE_KEY) || '{}');
    pagesData[page] = { ...pagesData[page], ...content, lastUpdated: new Date().toISOString() };
    localStorage.setItem(PAGES_STORAGE_KEY, JSON.stringify(pagesData));
    return pagesData[page];
  } catch (error) {
    console.error('Error updating page content:', error);
    return null;
  }
};

// Update contact information in localStorage
export const updateContactInfo = (contactData) => {
  try {
    const updatedData = { ...contactData, lastUpdated: new Date().toISOString() };
    localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(updatedData));
    return updatedData;
  } catch (error) {
    console.error('Error updating contact info:', error);
    return null;
  }
};

// CRUD Operations for Services
export const getServices = () => {
  try {
    const services = JSON.parse(localStorage.getItem(SERVICES_STORAGE_KEY) || '[]');
    return services.length > 0 ? services : getDefaultServices();
  } catch (error) {
    return getDefaultServices();
  }
};

export const addService = (service) => {
  try {
    const services = getServices();
    const newService = {
      id: Date.now().toString(),
      ...service,
      createdAt: new Date().toISOString()
    };
    services.push(newService);
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(services));
    return newService;
  } catch (error) {
    console.error('Error adding service:', error);
    return null;
  }
};

export const updateService = (id, updates) => {
  try {
    const services = getServices();
    const index = services.findIndex(s => s.id === id);
    if (index !== -1) {
      services[index] = { ...services[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(services));
      return services[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating service:', error);
    return null;
  }
};

export const deleteService = (id) => {
  try {
    const services = getServices();
    const filteredServices = services.filter(s => s.id !== id);
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(filteredServices));
    return true;
  } catch (error) {
    console.error('Error deleting service:', error);
    return false;
  }
};

// CRUD Operations for Values
export const getValues = () => {
  try {
    const values = JSON.parse(localStorage.getItem(VALUES_STORAGE_KEY) || '[]');
    return values.length > 0 ? values : getDefaultValues();
  } catch (error) {
    return getDefaultValues();
  }
};

export const addValue = (value) => {
  try {
    const values = getValues();
    const newValue = {
      id: Date.now().toString(),
      ...value,
      createdAt: new Date().toISOString()
    };
    values.push(newValue);
    localStorage.setItem(VALUES_STORAGE_KEY, JSON.stringify(values));
    return newValue;
  } catch (error) {
    console.error('Error adding value:', error);
    return null;
  }
};

export const updateValue = (id, updates) => {
  try {
    const values = getValues();
    const index = values.findIndex(v => v.id === id);
    if (index !== -1) {
      values[index] = { ...values[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(VALUES_STORAGE_KEY, JSON.stringify(values));
      return values[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating value:', error);
    return null;
  }
};

export const deleteValue = (id) => {
  try {
    const values = getValues();
    const filteredValues = values.filter(v => v.id !== id);
    localStorage.setItem(VALUES_STORAGE_KEY, JSON.stringify(filteredValues));
    return true;
  } catch (error) {
    console.error('Error deleting value:', error);
    return false;
  }
};

// CRUD Operations for Team
export const getTeamMembers = () => {
  try {
    const team = JSON.parse(localStorage.getItem(TEAM_STORAGE_KEY) || '[]');
    return team.length > 0 ? team : getDefaultTeam();
  } catch (error) {
    return getDefaultTeam();
  }
};

export const addTeamMember = (member) => {
  try {
    const team = getTeamMembers();
    const newMember = {
      id: Date.now().toString(),
      ...member,
      createdAt: new Date().toISOString()
    };
    team.push(newMember);
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(team));
    return newMember;
  } catch (error) {
    console.error('Error adding team member:', error);
    return null;
  }
};

export const updateTeamMember = (id, updates) => {
  try {
    const team = getTeamMembers();
    const index = team.findIndex(m => m.id === id);
    if (index !== -1) {
      team[index] = { ...team[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(team));
      return team[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating team member:', error);
    return null;
  }
};

export const deleteTeamMember = (id) => {
  try {
    const team = getTeamMembers();
    const filteredTeam = team.filter(m => m.id !== id);
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(filteredTeam));
    return true;
  } catch (error) {
    console.error('Error deleting team member:', error);
    return false;
  }
};

// CRUD Operations for Journey
export const getJourneyMilestones = () => {
  try {
    const journey = JSON.parse(localStorage.getItem(JOURNEY_STORAGE_KEY) || '[]');
    return journey.length > 0 ? journey : getDefaultJourney();
  } catch (error) {
    return getDefaultJourney();
  }
};

export const addJourneyMilestone = (milestone) => {
  try {
    const journey = getJourneyMilestones();
    const newMilestone = {
      id: Date.now().toString(),
      ...milestone,
      createdAt: new Date().toISOString()
    };
    journey.push(newMilestone);
    localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(journey));
    return newMilestone;
  } catch (error) {
    console.error('Error adding journey milestone:', error);
    return null;
  }
};

export const updateJourneyMilestone = (id, updates) => {
  try {
    const journey = getJourneyMilestones();
    const index = journey.findIndex(m => m.id === id);
    if (index !== -1) {
      journey[index] = { ...journey[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(journey));
      return journey[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating journey milestone:', error);
    return null;
  }
};

export const deleteJourneyMilestone = (id) => {
  try {
    const journey = getJourneyMilestones();
    const filteredJourney = journey.filter(m => m.id !== id);
    localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(filteredJourney));
    return true;
  } catch (error) {
    console.error('Error deleting journey milestone:', error);
    return false;
  }
};

// CRUD Operations for Case Studies
export const getCaseStudies = () => {
  try {
    const caseStudies = JSON.parse(localStorage.getItem(CASE_STUDIES_STORAGE_KEY) || '[]');
    return caseStudies.length > 0 ? caseStudies : getDefaultCaseStudies();
  } catch (error) {
    return getDefaultCaseStudies();
  }
};

export const addCaseStudy = (caseStudy) => {
  try {
    const caseStudies = getCaseStudies();
    const newCaseStudy = {
      id: Date.now().toString(),
      ...caseStudy,
      createdAt: new Date().toISOString()
    };
    caseStudies.push(newCaseStudy);
    localStorage.setItem(CASE_STUDIES_STORAGE_KEY, JSON.stringify(caseStudies));
    return newCaseStudy;
  } catch (error) {
    console.error('Error adding case study:', error);
    return null;
  }
};

export const updateCaseStudy = (id, updates) => {
  try {
    const caseStudies = getCaseStudies();
    const index = caseStudies.findIndex(cs => cs.id === id);
    if (index !== -1) {
      caseStudies[index] = { ...caseStudies[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(CASE_STUDIES_STORAGE_KEY, JSON.stringify(caseStudies));
      return caseStudies[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating case study:', error);
    return null;
  }
};

export const deleteCaseStudy = (id) => {
  try {
    const caseStudies = getCaseStudies();
    const filteredCaseStudies = caseStudies.filter(cs => cs.id !== id);
    localStorage.setItem(CASE_STUDIES_STORAGE_KEY, JSON.stringify(filteredCaseStudies));
    return true;
  } catch (error) {
    console.error('Error deleting case study:', error);
    return false;
  }
};

// CRUD Operations for Blog Posts
export const getBlogPosts = () => {
  try {
    const blogPosts = JSON.parse(localStorage.getItem(BLOG_POSTS_STORAGE_KEY) || '[]');
    return blogPosts.length > 0 ? blogPosts : getDefaultBlogPosts();
  } catch (error) {
    return getDefaultBlogPosts();
  }
};

export const addBlogPost = (blogPost) => {
  try {
    const blogPosts = getBlogPosts();
    const newBlogPost = {
      id: Date.now().toString(),
      ...blogPost,
      createdAt: new Date().toISOString()
    };
    blogPosts.push(newBlogPost);
    localStorage.setItem(BLOG_POSTS_STORAGE_KEY, JSON.stringify(blogPosts));
    return newBlogPost;
  } catch (error) {
    console.error('Error adding blog post:', error);
    return null;
  }
};

export const updateBlogPost = (id, updates) => {
  try {
    const blogPosts = getBlogPosts();
    const index = blogPosts.findIndex(bp => bp.id === id);
    if (index !== -1) {
      blogPosts[index] = { ...blogPosts[index], ...updates, updatedAt: new Date().toISOString() };
      localStorage.setItem(BLOG_POSTS_STORAGE_KEY, JSON.stringify(blogPosts));
      return blogPosts[index];
    }
    return null;
  } catch (error) {
    console.error('Error updating blog post:', error);
    return null;
  }
};

export const deleteBlogPost = (id) => {
  try {
    const blogPosts = getBlogPosts();
    const filteredBlogPosts = blogPosts.filter(bp => bp.id !== id);
    localStorage.setItem(BLOG_POSTS_STORAGE_KEY, JSON.stringify(filteredBlogPosts));
    return true;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    return false;
  }
};

// Initialize admin data if not exists (for frontend compatibility)
export const initializeContentData = () => {
  // Initialize contact details if not exists
  if (!localStorage.getItem(CONTACT_STORAGE_KEY)) {
    const contactData = getDefaultContactInfo();
    localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contactData));
  }

  // Initialize page data if not exists
  if (!localStorage.getItem(PAGES_STORAGE_KEY)) {
    const pagesData = {
      home: getDefaultPageContent('home'),
      services: getDefaultPageContent('services'),
      about: getDefaultPageContent('about'),
      caseStudies: getDefaultPageContent('caseStudies'),
      blog: getDefaultPageContent('blog'),
      contact: getDefaultPageContent('contact')
    };
    localStorage.setItem(PAGES_STORAGE_KEY, JSON.stringify(pagesData));
  }

  // Initialize services data if not exists
  if (!localStorage.getItem(SERVICES_STORAGE_KEY)) {
    const servicesData = getDefaultServices();
    localStorage.setItem(SERVICES_STORAGE_KEY, JSON.stringify(servicesData));
  }

  // Initialize values data if not exists
  if (!localStorage.getItem(VALUES_STORAGE_KEY)) {
    const valuesData = getDefaultValues();
    localStorage.setItem(VALUES_STORAGE_KEY, JSON.stringify(valuesData));
  }

  // Initialize team data if not exists
  if (!localStorage.getItem(TEAM_STORAGE_KEY)) {
    const teamData = getDefaultTeam();
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(teamData));
  }

  // Initialize journey data if not exists
  if (!localStorage.getItem(JOURNEY_STORAGE_KEY)) {
    const journeyData = getDefaultJourney();
    localStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(journeyData));
  }

  // Initialize case studies data if not exists
  if (!localStorage.getItem(CASE_STUDIES_STORAGE_KEY)) {
    const caseStudiesData = getDefaultCaseStudies();
    localStorage.setItem(CASE_STUDIES_STORAGE_KEY, JSON.stringify(caseStudiesData));
  }

  // Initialize blog posts data if not exists
  if (!localStorage.getItem(BLOG_POSTS_STORAGE_KEY)) {
    const blogPostsData = getDefaultBlogPosts();
    localStorage.setItem(BLOG_POSTS_STORAGE_KEY, JSON.stringify(blogPostsData));
  }
};

// Default page content function
const getDefaultPageContent = (page) => {
  const defaultContent = {
    home: {
      heroTitle: 'Transform Your Business with AI-Powered Solutions',
      heroSubtitle: 'We help small businesses and startups leverage cutting-edge Agentic AI technology to automate processes, boost efficiency, and drive unprecedented growth.',
      ctaText: 'Start Your AI Journey',
      featuresTitle: 'Why Choose Our AI Solutions?',
      featuresSubtitle: 'Comprehensive technology solutions designed for the modern business landscape.',
      statsTitle: 'Proven Results',
      statsSubtitle: 'Numbers that speak to our commitment to excellence.',
      testimonialsTitle: 'Client',
      testimonialsSubtitle: 'Hear from business leaders who have transformed their operations with our solutions.',
      ctaTitle: 'Ready to Transform Your Business?',
      ctaSubtitle: 'Join the ranks of successful businesses that have revolutionized their operations with our AI-powered solutions and strategic consulting.',
      ctaButtonText: 'Start Your Free Consultation'
    },
    services: {
      title: 'Our',
      subtitle: 'Comprehensive solutions designed to transform your business through innovative technology and strategic consulting.',
      processTitle: 'Our',
      processSubtitle: 'A proven methodology that ensures successful outcomes for every project.',
      additionalTitle: 'Additional',
      additionalSubtitle: 'Complementary services to maximize your success and ensure comprehensive support.',
      ctaTitle: 'Ready to Get Started?',
      ctaSubtitle: 'Let\'s discuss how our services can transform your business and drive unprecedented growth.',
      ctaButtonText: 'Get Free Basic Consultation'
    },
    about: {
      title: 'About',
      subtitle: 'We are passionate technologists and strategists dedicated to empowering small businesses and startups with the transformative power of AI and strategic consulting.',
      missionTitle: 'Our',
      mission: 'To democratize access to cutting-edge AI technology and strategic consulting, enabling small businesses and startups to compete and thrive in the digital economy.',
      missionDescription: 'We believe that every business, regardless of size, deserves access to the same transformative technologies that drive success for industry giants. Our mission is to level the playing field through innovative AI solutions and strategic guidance.',
      visionTitle: 'Our Vision',
      vision: 'To be the leading force in business transformation, where AI and human expertise combine to create unprecedented value and growth opportunities.',
      valuesTitle: 'Our',
      valuesSubtitle: 'These core values guide every decision we make and every solution we deliver.',
      teamTitle: 'Meet Our',
      teamSubtitle: 'Experienced professionals passionate about leveraging technology to drive business success.',
      journeyTitle: 'Our',
      journeySubtitle: 'Key milestones that have shaped our company and the value we deliver to clients.',
      ctaTitle: 'Join Our Success Story',
      ctaSubtitle: 'Ready to transform your business with our expertise? Let\'s start the conversation.',
      ctaButtonText: 'Get Free Basic Consultation'
    },
    caseStudies: {
      title: 'Case',
      subtitle: 'Real success stories showcasing how our AI solutions and strategic consulting have transformed businesses across industries.',
      impactTitle: 'Industry',
      impactSubtitle: 'Our proven track record across diverse industries demonstrates the versatility and effectiveness of our solutions.',
      storiesTitle: 'Success',
      storiesSubtitle: 'Discover how we\'ve helped businesses achieve remarkable transformations and measurable results.',
      frameworkTitle: 'Our Success',
      frameworkSubtitle: 'The proven methodology behind every successful transformation.',
      ctaTitle: 'Ready to Write Your Success Story?',
      ctaSubtitle: 'Join the ranks of businesses that have achieved extraordinary results with our solutions.',
      ctaButtonText: 'Get Free Basic Consultation'
    },
    blog: {
      title: 'OHA System',
      subtitle: 'Insights, strategies, and best practices for leveraging AI and technology to transform your business operations and drive growth.',
      featuredTitle: 'Featured',
      newsletterTitle: 'Stay Updated with Our Latest Insights',
      newsletterSubtitle: 'Get exclusive content, industry insights, and practical tips delivered directly to your inbox.',
      newsletterButtonText: 'Subscribe',
      newsletterDisclaimer: 'No spam, unsubscribe at any time. We respect your privacy.'
    },
    contact: {
      title: 'Contact',
      subtitle: 'Ready to transform your business? Let\'s start the conversation about how our AI solutions can drive your success.',
      contactTitle: 'Get in',
      contactSubtitle: 'Multiple ways to reach us - choose what works best for you.',
      formTitle: 'Get Your Free Basic Consultation',
      formSubtitle: 'Fill out the form below and we\'ll get back to you within 24 hours to discuss how we can help transform your business.',
      whyChooseTitle: 'Why Choose OHA System?',
      nextStepsTitle: 'What Happens Next?'
    }
  };
  
  return defaultContent[page] || {};
};

// Default contact info function
const getDefaultContactInfo = () => ({
  email: 'contact@ohasystem.com',
  phone: '+1 (555) 123-4567',
  address: 'San Francisco, CA',
  businessHours: 'Mon-Fri: 9AM-6PM PST',
  socialMedia: {
    twitter: 'https://twitter.com/ohasystem',
    linkedin: 'https://linkedin.com/company/ohasystem',
    facebook: 'https://facebook.com/ohasystem',
    instagram: 'https://instagram.com/ohasystem'
  }
});

// Initialize on import
initializeContentData();